// DAQI Columns Responsive Layout Handler
//
// SOLUTION IMPLEMENTED: Dividers now use CSS-only flexbox positioning in _daqitable.scss
// - Both labels and dividers use the same flex ratios for perfect alignment
// - No JavaScript delays when resizing browser window
// - This JavaScript remains for backwards compatibility and advanced testing
//
// Development Mode: To manually test CSS custom properties in dev tools,
// add 'data-manual-dividers' attribute to the .daqi-numbered container:
// Example: document.querySelector('.daqi-numbered').setAttribute('data-manual-dividers', 'true')
// This will disable automatic JavaScript positioning and allow manual CSS testing.
//
// Only register DOM event listeners if running in a browser
if (typeof document !== 'undefined' && typeof window !== 'undefined') {
  // Always recalculate when the tab is shown (including tab 1)
  document.addEventListener('DOMContentLoaded', () => {
    setDaqiColumns()
    setTimeout(setDaqiColumns, 0)

    // Initialize accessibility features after DAQI columns are set
    setTimeout(() => {
      if (
        window.daqiAccessibility &&
        window.daqiAccessibility.enhanceDaqiBarAccessibility
      ) {
        window.daqiAccessibility.enhanceDaqiBarAccessibility()
      }
    }, 100)
  })

  // Listen for tab switches and force recalculation - only for DAQI tabs
  document.addEventListener('click', (e) => {
    if (
      e.target &&
      e.target.classList &&
      e.target.classList.contains('govuk-tabs__tab') &&
      e.target.closest('.daqi-tabs') // Only apply to DAQI tabs component
    ) {
      setTimeout(setDaqiColumns, 40)

      // Re-enhance accessibility after tab change
      setTimeout(() => {
        if (
          window.daqiAccessibility &&
          window.daqiAccessibility.enhanceDaqiBarAccessibility
        ) {
          window.daqiAccessibility.enhanceDaqiBarAccessibility()
        }
      }, 100)
    }
  })

  // Add window global for testing - disable automatic updates
  window.disableDaqiAutoUpdates = false

  // Add debugging and testing utilities
  window.daqiTestDividers = function (d1, d2, d3) {
    const container = document.querySelector('.daqi-numbered')
    if (!container) {
      return
    }
    container.style.setProperty('--daqi-divider-1', d1 + 'px')
    container.style.setProperty('--daqi-divider-2', d2 + 'px')
    container.style.setProperty('--daqi-divider-3', d3 + 'px')

    // Force style recalculation
    const labelsElement = container.querySelector('.daqi-labels')
    if (labelsElement) {
      labelsElement.style.display = 'none'
      labelsElement.offsetHeight // Force reflow
      labelsElement.style.display = ''
    }
  }

  window.daqiShowCurrentValues = function () {
    const container = document.querySelector('.daqi-numbered')
    if (!container) {
      return
    }

    const computedStyle = getComputedStyle(container)

    // Also check the pseudo-element's background-position
    const labels = container.querySelector('.daqi-labels')
    if (labels) {
      const beforeStyle = getComputedStyle(labels, '::before')
    }
  }

  // Add resize listener with debouncing for window resize
  const debouncedResize = debounce(() => {
    if (!window.disableDaqiAutoUpdates) {
      setDaqiColumns()
    }
  }, 150)

  window.addEventListener('resize', debouncedResize)

  // Add ResizeObserver to watch for container size changes (when tabs resize dynamically)
  if (typeof ResizeObserver !== 'undefined') {
    const debouncedContainerResize = debounce(() => {
      if (!window.disableDaqiAutoUpdates) {
        setDaqiColumns()
      }
    }, 100)

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect
      }
      debouncedContainerResize()
    })

    // Observe DAQI containers AND their parent tabs containers
    const observeContainers = () => {
      // Observe DAQI containers
      const daqiContainers = document.querySelectorAll('.daqi-numbered')

      daqiContainers.forEach((container, index) => {
        resizeObserver.observe(container)
      })

      // Also observe tabs containers that might contain DAQI
      const tabsContainers = document.querySelectorAll('.defra-aq-tabs')

      tabsContainers.forEach((tabsContainer, index) => {
        resizeObserver.observe(tabsContainer)
      })

      // Also observe grid columns that might contain tabs
      const gridColumns = document.querySelectorAll(
        '.govuk-grid-column-full-until-desktop, .govuk-grid-column-two-thirds-from-desktop'
      )

      gridColumns.forEach((gridColumn, index) => {
        if (gridColumn.querySelector('.daqi-numbered')) {
          resizeObserver.observe(gridColumn)
        }
      })
    }

    // Observe containers immediately if DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', observeContainers)
    } else {
      observeContainers()
    }

    // Also add a mutation observer to catch dynamically added elements
    const mutationObserver = new MutationObserver((mutations) => {
      let shouldReobserve = false
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (
              node.nodeType === 1 &&
              (node.classList?.contains('daqi-numbered') ||
                node.classList?.contains('defra-aq-tabs') ||
                node.querySelector?.('.daqi-numbered'))
            ) {
              shouldReobserve = true
            }
          })
        }
      })
      if (shouldReobserve) {
        observeContainers()
      }
    })

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    })
  } else {
    // ResizeObserver not supported - fallback to window resize only
  }
}
// daqi-columns.js
// Measure DAQI bar segment widths and set a CSS variable so labels and bar share column sizing

function debounce(fn, wait = 100) {
  let t
  return (...args) => {
    clearTimeout(t)
    t = setTimeout(() => fn(...args), wait)
  }
}

function setDaqiColumns() {
  // Check if automatic updates are disabled for testing
  if (typeof window !== 'undefined' && window.disableDaqiAutoUpdates) {
    return
  }

  // Find all DAQI containers and process each one
  const containers = document.querySelectorAll('.daqi-numbered')
  if (!containers || containers.length === 0) return

  containers.forEach((container) => {
    // Skip if container is explicitly hidden
    try {
      const cs = window.getComputedStyle(container)
      if (cs.display === 'none' || cs.visibility === 'hidden') {
        return // Skip this container but continue with others
      }
    } catch (e) {
      // In some test environments window may be undefined or getComputedStyle
      // may throw. Fail safe by continuing in that case.
    }

    const segments = container.querySelectorAll('.daqi-bar-segment')
    if (!segments || segments.length === 0) return

    // Responsive behaviour: Use container width instead of viewport width
    // to ensure dynamic behavior when tabs container shrinks on any viewport size
    const containerWidth = findAvailableWidth(container)
    const viewportWidth =
      (typeof window !== 'undefined' && window.innerWidth) ||
      document.documentElement.clientWidth

    // Define thresholds based on container width for dynamic responsive behavior
    const NARROW_CONTAINER_THRESHOLD = 500 // Below this container width: apply mobile-style layout
    const MEDIUM_CONTAINER_THRESHOLD = 640 // Container width where adjustments start

    // Use container width to determine layout instead of viewport width
    const containerIsNarrow = containerWidth <= NARROW_CONTAINER_THRESHOLD
    const containerNeedsAdjustments =
      containerWidth <= MEDIUM_CONTAINER_THRESHOLD

    // Helper function to find available width
    function findAvailableWidth(el) {
      const MIN_REASONABLE = 40
      let current = el
      for (let i = 0; i < 8 && current; i++) {
        const w =
          current.clientWidth ||
          Math.round(current.getBoundingClientRect().width)
        if (w && w > MIN_REASONABLE) return w
        current = current.parentElement
      }
      // fallback to viewport width
      return (
        (typeof window !== 'undefined' && window.innerWidth) ||
        document.documentElement.clientWidth ||
        0
      )
    }

    // Development mode: Add 'data-manual-dividers' attribute to container to disable automatic updates
    if (container.hasAttribute('data-manual-dividers')) {
      return
    }

    // Debug container-based detection

    // Universal divider calculation approach - works for all layouts
    // Let CSS handle the responsive layout, JavaScript only positions dividers
    const calculateDividers = (
      containerWidthParam,
      containerNeedsAdjustmentsParam
    ) => {
      // Use responsive gap based on container width to match CSS
      let GAP
      if (containerWidthParam <= 360) {
        GAP = 1 // 1px gap for very narrow containers
      } else if (containerWidthParam <= 430) {
        GAP = 2 // 2px gap for narrow containers
      } else {
        GAP = 3 // 3px gap for wider containers
      }

      const segmentWidths = Array.from(segments).map((seg, index) => {
        const rect = seg.getBoundingClientRect()
        // Use more precise rounding to handle sub-pixel measurements better
        const preciseWidth = rect.width
        const width = Math.round(preciseWidth)

        // Special debugging for iPad Mini
        if (viewportWidth === 768) {
          // Debug info removed for production
        }

        // Fallback calculation if getBoundingClientRect returns invalid values
        if (width <= 0 || width > container.clientWidth) {
          const innerContainerWidth =
            container.clientWidth || findAvailableWidth(container)
          const cellIndex = Array.from(segments).indexOf(seg)

          // Assume reasonably distributed widths based on container
          if (cellIndex === 9) {
            // Cell 10 (last cell)
            const fallbackWidth = Math.round(innerContainerWidth * 0.2) // ~20% for last cell
            return fallbackWidth
          } else {
            // Cells 1-9
            const fallbackWidth = Math.round(innerContainerWidth * 0.08) // ~8% each for first 9 cells
            return fallbackWidth
          }
        }
        return width
      })

      // Calculate base divider positions after segments 3, 6, and 9
      const baseDividerPositions = []
      for (const n of [3, 6, 9]) {
        const sum = segmentWidths.slice(0, n).reduce((s, v) => s + v, 0)
        const gaps = GAP * (n - 1)
        baseDividerPositions.push(sum + gaps)
      }

      // Apply responsive adjustments based on container width
      // Move dividers for better positioning when container is narrow
      let divider1Adjustment, divider2Adjustment, divider3Adjustment

      if (containerNeedsAdjustmentsParam) {
        // Container is narrow - apply responsive adjustments
        if (containerWidthParam <= 430) {
          // Very narrow container: apply mobile-style adjustments
          if (viewportWidth === 430) {
            // iPhone 14 Pro Max specific: move dividers left
            divider1Adjustment = -1 // Move another 1px left from 0
            divider2Adjustment = 0 // Move another 1px left from 1
            divider3Adjustment = 1 // Move another 1px left from 2
          } else {
            // Other narrow containers: move dividers further left for better alignment
            divider1Adjustment = -3 // Move another 1px left from -2
            divider2Adjustment = -3 // Move another 1px left from -2
            divider3Adjustment = -4 // Move another 1px left from -3
          }
        } else {
          // Medium narrow container: apply moderate adjustments moved another 1px left
          divider1Adjustment = -2 // Move another 1px left from -1
          divider2Adjustment = -3 // Move another 1px left from -2
          divider3Adjustment = -3 // Move another 1px left from -2
        }
      } else {
        // Wide container: move all dividers another 1px left
        divider1Adjustment = -2 // Move another 1px left from -1
        divider2Adjustment = -2 // Move another 1px left from -1
        divider3Adjustment = -2 // Move another 1px left from -1

        // Special handling for iPad Mini (768px viewport) if dividers seem problematic
        if (viewportWidth === 768) {
          // Ensure all base positions are valid before applying
          if (baseDividerPositions.some((pos) => pos <= 0 || isNaN(pos))) {
            // Force recalculation with fallback values
            const innerContainerWidth =
              container.clientWidth || findAvailableWidth(container)
            baseDividerPositions[0] = Math.round(innerContainerWidth * 0.24) // ~24% for first 3 segments
            baseDividerPositions[1] = Math.round(innerContainerWidth * 0.48) // ~48% for first 6 segments
            baseDividerPositions[2] = Math.round(innerContainerWidth * 0.72) // ~72% for first 9 segments
          }
        }
      }

      const divider1Value =
        Math.round(baseDividerPositions[0]) + divider1Adjustment + 'px'
      const divider2Value =
        Math.round(baseDividerPositions[1]) + divider2Adjustment + 'px'
      const divider3Value =
        Math.round(baseDividerPositions[2]) + divider3Adjustment + 'px'

      // Special handling for zoom-related sub-pixel issues
      if (viewportWidth === 768) {
        const rawDivider2 = baseDividerPositions[1] + divider2Adjustment
        // Debug info removed for production
      }

      // Debug: Check for iPad Mini issues (768px viewport)
      if (viewportWidth === 768) {
        // Get zoom level information
        const zoomLevel = window.devicePixelRatio || 1
        const visualViewport = window.visualViewport
        const zoomFactor = visualViewport ? visualViewport.scale : 1

        // Debug info removed for production

        // Debug info removed for production

        // Log current CSS custom properties before setting new ones
        const currentDivider2 =
          container.style.getPropertyValue('--daqi-divider-2')
      }

      // Ensure all divider values are valid (not 0, negative, or NaN)
      // Also handle sub-pixel rendering issues that can occur at different zoom levels
      const validateDividerValue = (
        value,
        fallback,
        name,
        isZoomProblematic = false
      ) => {
        const numValue = parseInt(value)
        if (isNaN(numValue) || numValue <= 0) {
          return fallback + 'px'
        }

        // Special handling for zoom-related sub-pixel issues
        // Add a small offset to ensure visibility at different zoom levels
        let finalValue = Math.round(numValue)

        if (isZoomProblematic && name === 'divider2') {
          // For problematic zoom levels, add a 1px safety margin to divider 2
          finalValue = finalValue + 1
        }

        return finalValue + 'px'
      }

      const containerWidth =
        container.clientWidth || findAvailableWidth(container)

      // Detect zoom-related issues that might affect divider visibility
      const zoomLevel = window.devicePixelRatio || 1
      const visualViewport = window.visualViewport
      const zoomFactor = visualViewport ? visualViewport.scale : 1
      const isZoomProblematic = zoomFactor < 1 || zoomLevel !== 1 // Any zoom other than 100%

      if (viewportWidth === 768 && isZoomProblematic) {
        // Zoom issue detection removed
      }

      const validDivider1 = validateDividerValue(
        divider1Value,
        Math.round(containerWidth * 0.24),
        'divider1',
        isZoomProblematic
      )
      const validDivider2 = validateDividerValue(
        divider2Value,
        Math.round(containerWidth * 0.48),
        'divider2',
        isZoomProblematic
      )
      const validDivider3 = validateDividerValue(
        divider3Value,
        Math.round(containerWidth * 0.72),
        'divider3',
        isZoomProblematic
      )

      container.style.setProperty('--daqi-divider-1', validDivider1)
      container.style.setProperty('--daqi-divider-2', validDivider2)
      container.style.setProperty('--daqi-divider-3', validDivider3)

      // Additional iPad Mini debugging after setting properties
      if (viewportWidth === 768) {
        // Debug info removed for production

        // Verify the properties were actually set by reading them back
        setTimeout(() => {
          const verifyDivider1 =
            container.style.getPropertyValue('--daqi-divider-1')
          const verifyDivider2 =
            container.style.getPropertyValue('--daqi-divider-2')
          const verifyDivider3 =
            container.style.getPropertyValue('--daqi-divider-3')

          // Debug info removed for production

          // Check if divider 2 is missing or invalid
          if (
            !verifyDivider2 ||
            verifyDivider2 === '0px' ||
            parseInt(verifyDivider2) <= 0
          ) {
            // Force set a reasonable fallback
            const fallbackPosition =
              Math.round(container.clientWidth * 0.48) + 'px'
            container.style.setProperty('--daqi-divider-2', fallbackPosition)
          }

          // Additional fix for zoom issues: Force browser to use hardware acceleration
          // This can help with sub-pixel rendering consistency
          if (isZoomProblematic) {
            const labelsElement = container.querySelector('.daqi-labels')
            if (labelsElement) {
              labelsElement.style.transform = 'translateZ(0)' // Force hardware acceleration
              labelsElement.style.backfaceVisibility = 'hidden' // Optimize rendering

              // Also add a zoom-issue class for CSS-based fixes
              container.classList.add('daqi-zoom-fix')
            }
          }
        }, 50)
      }

      // Clear --daqi-columns for mobile viewports (≤640px), maintain for desktop
      if (containerNeedsAdjustmentsParam) {
        container.style.removeProperty('--daqi-columns') // Clear for mobile flexbox layout

        if (viewportWidth === 430) {
          // iPhone 14 Pro Max adjustments applied (right positioning)
        } else {
          // Mobile layout adjustments applied (left positioning)
        }
      } else {
        // Desktop layout maintained - debug info removed for production
      }
    }

    // Execute divider calculation
    calculateDividers(containerWidth, containerNeedsAdjustments)

    // Also apply with a small delay to handle any timing issues
    setTimeout(
      () => calculateDividers(containerWidth, containerNeedsAdjustments),
      16
    )
  }) // Close the forEach loop
}

const debounced = debounce(setDaqiColumns, 120)

function init() {
  // run after DOMContentLoaded
  // guard against being initialised more than once (e.g. if application
  // initialisation is run again or the module is re-imported). We store
  // a flag on the container element so observers/listeners aren't doubled.
  const container = document.querySelector('.daqi-numbered')
  if (!container) return
  if (container.__daqi_initialized) return
  container.__daqi_initialized = true

  // Recalculate after DOM is ready and all tab content is present
  setDaqiColumns()
  // Schedule another recalculation after all content (including forecast) is rendered
  setTimeout(setDaqiColumns, 0)
  // If MutationObserver is available, watch for changes in tab panel content
  if (typeof MutationObserver !== 'undefined') {
    const panel =
      container.closest('.govuk-tabs__panel') || container.parentElement
    if (panel) {
      const mo = new MutationObserver(() => setTimeout(setDaqiColumns, 0))
      mo.observe(panel, { childList: true, subtree: true })
      container.__daqi_mutation_observer = mo
    }
  }
  // Use ResizeObserver where available for fine-grained updates
  if (typeof ResizeObserver !== 'undefined') {
    const container = document.querySelector('.daqi-numbered')
    if (container) {
      const ro = new ResizeObserver(debounced)
      // Observe the bar and the container in case children change
      const bar = container.querySelector('.daqi-bar')
      if (bar) ro.observe(bar)
      ro.observe(container)
      // keep a reference for potential disconnect (not strictly necessary)
      container.__daqi_resize_observer = ro
      // Also listen for window resize events in addition to ResizeObserver.
      // Some browsers may not dispatch ResizeObserver entries for viewport
      // changes in all cases; having a window resize listener guarantees
      // we recalc when the viewport width crosses the grouping threshold.
      window.addEventListener('resize', debounced)
      // Re-run measurements when the active tab/panel changes via URL hash.
      // We schedule via setTimeout so that other handlers (eg. tabs show/hide)
      // have finished mutating the DOM before measuring.
      window.addEventListener('hashchange', () => setTimeout(debounced, 40))
      // pageshow is fired when a page is restored from bfcache (back/forward).
      // Recompute there as DOM may be restored without a new load event.
      window.addEventListener('pageshow', debounced)
    }
  } else {
    window.addEventListener('resize', debounced)
    window.addEventListener('hashchange', () => setTimeout(debounced, 40))
    window.addEventListener('pageshow', debounced)
  }

  // Also listen for font loading which can change widths
  if (document.fonts) {
    document.fonts.ready.then(() => {
      setDaqiColumns()
      setTimeout(setDaqiColumns, 0)
    })
  }
}

export { setDaqiColumns, debounce }
export default { init, setDaqiColumns }
